(() => {
  const on = (type, selector, handler) => {
    document.addEventListener(type, (event) => {
      const target = event.target.closest(selector);
      if (target) handler(event, target);
    });
  };

  const luxeSearchEndpoint = 'https://ldj-search-shopify.onrender.com/api/search';
  const luxeSearchEventsEndpoint = 'https://ldj-search-shopify.onrender.com/api/search-events';
  const luxePopularSearchesEndpoint = 'https://ldj-search-shopify.onrender.com/api/popular-searches';
  const luxeAnalyticsSessionKey = 'luxe:search-analytics-session';
  const luxeAnalyticsVisitorKey = 'luxe:search-analytics-visitor';
  const recentSearchesKey = 'luxe:recent-searches';
  const recentProductsKey = 'luxe:recent-products';
  const popularSearchesKey = 'luxe:popular-searches';
  const fallbackPopularSearches = ['chanel', 'hermes', 'mini kelly', 'louis vuitton', 'dior'];
  const luxeMoney = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  });

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  })[character]);

  const searchArrowIcon = '<svg class="predictive-search__arrow-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M5 4h7v7" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"></path><path d="M4 12 12 4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"></path></svg>';

  const formatPrice = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    return luxeMoney.format(number);
  };

  const readStoredList = (key) => {
    try {
      const value = JSON.parse(window.localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (_error) {
      return [];
    }
  };

  const writeStoredList = (key, value) => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (_error) {
      // Storage can be unavailable in private browsing. Search still works.
    }
  };

  const normalizeQuery = (value) => String(value || '').trim().replace(/\s+/g, ' ');

  const isTaxonomyTerm = (value) => {
    const query = normalizeQuery(value).toLowerCase();
    if (!query) return false;
    return query.includes('>')
      || query.includes('apparel & accessories')
      || query.includes('handbags, wallets')
      || query.includes('google product category')
      || query.includes('google_product_category');
  };

  const isCleanSearchTerm = (value) => {
    const query = normalizeQuery(value);
    if (!query || query.length > 42) return false;
    return !isTaxonomyTerm(query);
  };

  const searchIntentDelay = 900;
  const searchIntentDedupeMs = 30 * 1000;
  const shortSearchIntentTerms = new Set(['lv']);
  const searchApiReadOnlyTriggers = new Set([
    'predictive_typeahead',
    'search_page_typeahead',
    'sort_change',
    'filter_add',
    'filter_remove',
    'filter_chip_remove',
    'infinite_scroll',
    'pagination',
    'popstate'
  ]);

  const shouldTrackSearchIntent = (value) => {
    const query = normalizeQuery(value).toLowerCase();
    return isCleanSearchTerm(query) && (query.length >= 3 || shortSearchIntentTerms.has(query));
  };

  const resultCountFor = (payload) => {
    const count = Number(payload?.found);
    return Number.isFinite(count) ? count : null;
  };

  const searchIntentTypeFor = (payload) => resultCountFor(payload) === 0 ? 'search_no_results' : 'search';

  const normalizeProductUrl = (value) => {
    if (!value) return '';
    try {
      return new URL(value, window.location.origin).pathname.replace(/\/$/, '') || '/';
    } catch (_error) {
      return String(value).split('?')[0].replace(/^https?:\/\/[^/]+/i, '').replace(/\/$/, '');
    }
  };

  const cleanRecentSearches = () => {
    const seen = new Set();
    const cleaned = [];
    for (const value of readStoredList(recentSearchesKey)) {
      const query = normalizeQuery(value);
      const key = query.toLowerCase();
      if (!isCleanSearchTerm(query) || seen.has(key)) continue;
      seen.add(key);
      cleaned.push(query);
    }
    writeStoredList(recentSearchesKey, cleaned.slice(0, 6));
    return cleaned;
  };

  const cleanRecentProducts = () => {
    const seen = new Set();
    const cleaned = [];
    for (const product of readStoredList(recentProductsKey)) {
      const title = normalizeQuery(product?.title);
      const url = normalizeProductUrl(product?.url);
      if (!title || !url || seen.has(url)) continue;
      seen.add(url);
      cleaned.push({ title, url, image: product.image || '' });
    }
    writeStoredList(recentProductsKey, cleaned.slice(0, 8));
    return cleaned;
  };

  const searchUrlFor = (query) => `/search?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last`;

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const activeGsap = () => {
    if (prefersReducedMotion()) return null;
    return window.gsap || null;
  };

  // Entrance for search results and filters: Web Animations run on the compositor and
  // write no inline styles, so nothing reads computed style per element and Shopify's
  // pixel manager (which re-serializes every inline style change) stays idle.
  const animateIn = (elements, options = {}) => {
    if (prefersReducedMotion() || !elements.length || typeof Element === 'undefined' || !Element.prototype.animate) return;
    const {
      duration = 0.22,
      stagger = 0.018,
      y = 10,
      scale = 1,
      limit = 14,
      easing = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)'
    } = options;
    elements.slice(0, limit).forEach((element, index) => {
      element.animate([
        { opacity: 0, transform: `translateY(${y}px)${scale === 1 ? '' : ` scale(${scale})`}` },
        { opacity: 1, transform: 'none' }
      ], { duration: duration * 1000, delay: index * stagger * 1000, easing, fill: 'backwards' });
    });
  };

  const productCardGallerySelector = '[data-product-card-gallery]';
  const productCardGalleryStates = new WeakMap();
  const collectionRailImageCache = new Map();

  const syncGalleryIndicator = (state) => {
    if (!state.indicator || !state.count) return;
    state.indicator.style.width = `${100 / state.count}%`;
    state.indicator.style.transform = `translateX(${state.index * 100}%)`;
  };

  const normalizeGalleryIndex = (index, count) => ((index % count) + count) % count;

  const setGalleryScrollIndex = (state, index, smooth = false) => {
    const width = Math.max(1, state.track.clientWidth || state.gallery.clientWidth || 1);
    state.index = normalizeGalleryIndex(index, state.count);
    state.isSnapping = true;
    const nextLeft = state.index * width;
    if (smooth && !prefersReducedMotion()) {
      state.track.scrollTo({ left: nextLeft, behavior: 'smooth' });
    } else {
      state.track.scrollLeft = nextLeft;
    }
    window.setTimeout(() => {
      state.isSnapping = false;
    }, smooth ? 320 : 40);
    syncGalleryIndicator(state);
  };

  const setGalleryRawPosition = (state, rawPosition) => {
    const width = Math.max(1, state.track.clientWidth || state.gallery.clientWidth || 1);
    state.isSnapping = true;
    const previousBehavior = state.track.style.scrollBehavior;
    state.track.style.scrollBehavior = 'auto';
    state.track.scrollLeft = rawPosition * width;
    window.requestAnimationFrame(() => {
      state.track.style.scrollBehavior = previousBehavior;
      state.isSnapping = false;
    });
  };

  const settleGalleryScroll = (state) => {
    const width = Math.max(1, state.track.clientWidth || state.gallery.clientWidth || 1);
    const rawPosition = Math.round(state.track.scrollLeft / width);

    if (rawPosition >= (state.count * 2) - 1) {
      state.index = state.count - 1;
      setGalleryRawPosition(state, state.count - 1);
      syncGalleryIndicator(state);
      return;
    }

    state.index = normalizeGalleryIndex(rawPosition, state.count);
    syncGalleryIndicator(state);
  };

  const initProductCardGalleries = (root = document) => {
    const galleries = root.matches?.(productCardGallerySelector)
      ? [root]
      : Array.from(root.querySelectorAll(productCardGallerySelector));

    for (const gallery of galleries) {
      if (productCardGalleryStates.has(gallery)) continue;
      const track = gallery.querySelector('[data-product-card-gallery-track]');
      const indicator = gallery.querySelector('[data-product-card-gallery-indicator]');
      const count = Number(gallery.dataset.slideCount) || 0;
      if (!track || count < 2) continue;

      const state = {
        count,
        gallery,
        index: 0,
        indicator,
        isSnapping: false,
        settleTimer: null,
        track
      };

      productCardGalleryStates.set(gallery, state);
      gallery.dataset.galleryReady = 'true';
      syncGalleryIndicator(state);

      track.addEventListener('scroll', () => {
        const width = Math.max(1, track.clientWidth || gallery.clientWidth || 1);
        const rawPosition = Math.round(track.scrollLeft / width);
        state.index = normalizeGalleryIndex(rawPosition, state.count);
        syncGalleryIndicator(state);
        window.clearTimeout(state.settleTimer);
        state.settleTimer = window.setTimeout(() => settleGalleryScroll(state), state.isSnapping ? 180 : 90);
      }, { passive: true });

      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(() => setGalleryScrollIndex(state, state.index, false));
        resizeObserver.observe(gallery);
        state.resizeObserver = resizeObserver;
      }
    }
  };

  const imageFromSearchHit = (hit) => {
    if (!hit) return '';
    if (hit.image) return hit.image;
    if (hit.secondary_image) return hit.secondary_image;
    if (Array.isArray(hit.images)) {
      const image = hit.images.find(Boolean);
      if (!image) return '';
      if (typeof image === 'string') return image;
      return image.src || image.url || image.image || '';
    }
    return '';
  };

  const fetchCollectionRailImage = async (endpoint, query) => {
    const cacheKey = `${endpoint}::${query}`.toLowerCase();
    if (collectionRailImageCache.has(cacheKey)) return collectionRailImageCache.get(cacheKey);

    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('per_page', '1');
    url.searchParams.set('analytics', 'false');
    url.searchParams.set('source', 'collection_rail');

    const imagePromise = fetch(url, { headers: { Accept: 'application/json' } })
      .then((response) => response.ok ? response.json() : null)
      .then((payload) => imageFromSearchHit(payload?.hits?.[0]))
      .catch(() => '');

    collectionRailImageCache.set(cacheKey, imagePromise);
    return imagePromise;
  };

  const initCollectionRailImages = (root = document) => {
    const rails = root.matches?.('[data-collection-rail]')
      ? [root]
      : Array.from(root.querySelectorAll('[data-collection-rail]'));

    rails.forEach((rail) => {
      if (rail.dataset.railImagesReady === 'true') return;
      const endpoint = rail.dataset.searchEndpoint;
      if (!endpoint) return;
      rail.dataset.railImagesReady = 'true';

      const cards = Array.from(rail.querySelectorAll('[data-collection-rail-card]'))
        .filter((card) => {
          const target = card.querySelector('[data-collection-rail-image-target]');
          return target && !target.querySelector('img');
        });

      const hydrate = () => {
        cards.forEach(async (card) => {
          const query = normalizeQuery(card.dataset.collectionRailQuery || card.textContent || '');
          const target = card.querySelector('[data-collection-rail-image-target]');
          if (!query || !target) return;
          const src = await fetchCollectionRailImage(endpoint, query);
          if (!src || target.querySelector('img')) {
            target.classList.remove('collection-rail__media--pending', 'luxe-skeleton');
            return;
          }

          const img = document.createElement('img');
          img.className = 'collection-rail__image';
          img.loading = 'lazy';
          img.alt = card.textContent.trim();
          img.src = src;
          target.appendChild(img);
          target.classList.remove('collection-rail__media--pending', 'luxe-skeleton');
        });
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(hydrate, { timeout: 900 });
      } else {
        window.setTimeout(hydrate, 120);
      }
    });
  };

  const loadCustomerPrivacyApi = () => {
    if (window.Shopify?.customerPrivacy || !window.Shopify?.loadFeatures) return;
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      () => {}
    );
  };

  const analyticsEndpointFor = (endpoint) => {
    try {
      const url = new URL(endpoint || luxeSearchEndpoint, window.location.origin);
      const nextPath = url.pathname.replace(/\/api\/search\/?$/, '/api/search-events');
      if (nextPath === url.pathname) return luxeSearchEventsEndpoint;
      url.pathname = nextPath;
      return url.toString();
    } catch (_error) {
      return luxeSearchEventsEndpoint;
    }
  };

  const popularSearchesEndpointFor = (endpoint) => {
    try {
      const url = new URL(endpoint || luxeSearchEndpoint, window.location.origin);
      const nextPath = url.pathname.replace(/\/api\/search\/?$/, '/api/popular-searches');
      if (nextPath === url.pathname) return luxePopularSearchesEndpoint;
      url.pathname = nextPath;
      return url.toString();
    } catch (_error) {
      return luxePopularSearchesEndpoint;
    }
  };

  const analyticsConfig = () => window.LuxeSearchAnalytics || {};

  const consentSnapshot = () => {
    const privacy = window.Shopify?.customerPrivacy;
    const safeCall = (methodName) => {
      try {
        return typeof privacy?.[methodName] === 'function' ? privacy[methodName]() === true : false;
      } catch (_error) {
        return false;
      }
    };

    return {
      consent_analytics: safeCall('analyticsProcessingAllowed'),
      consent_marketing: safeCall('marketingAllowed')
    };
  };

  const makeAnalyticsId = (prefix) => {
    if (window.crypto?.randomUUID) return `${prefix}_${window.crypto.randomUUID()}`;
    return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  };

  const storedAnalyticsId = (storage, key, prefix) => {
    try {
      const existing = storage.getItem(key);
      if (existing) return existing;
      const next = makeAnalyticsId(prefix);
      storage.setItem(key, next);
      return next;
    } catch (_error) {
      return makeAnalyticsId(prefix);
    }
  };

  const analyticsIdentity = () => {
    const consent = consentSnapshot();
    if (!consent.consent_analytics) return consent;

    return {
      ...consent,
      session_id: storedAnalyticsId(window.sessionStorage, luxeAnalyticsSessionKey, 'sess'),
      visitor_id: storedAnalyticsId(window.localStorage, luxeAnalyticsVisitorKey, 'vis'),
      customer_id: analyticsConfig().customerReference || ''
    };
  };

  const analyticsMetadata = (extra = {}) => ({
    path: window.location.pathname,
    url: window.location.href,
    referrer: document.referrer,
    ...extra
  });

  const appendSearchAnalyticsParams = (url, extra = {}) => {
    const params = {
      shop: analyticsConfig().shop || '',
      ...analyticsIdentity(),
      ...extra
    };

    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '') continue;
      url.searchParams.set(key, String(value));
    }
  };

  const trackSearchEvent = (type, payload = {}, endpoint = luxeSearchEventsEndpoint) => {
    const body = {
      type,
      shop: analyticsConfig().shop || '',
      source: payload.source || 'storefront',
      trigger: payload.trigger || 'client',
      ...analyticsIdentity(),
      ...payload,
      metadata: analyticsMetadata(payload.metadata)
    };
    const target = endpoint || luxeSearchEventsEndpoint;
    const json = JSON.stringify(body);

    // sendBeacon always sends credentials, which the cross-origin search API
    // rejects (wildcard CORS). Only use it for same-origin targets.
    let sameOrigin = false;
    try {
      sameOrigin = new URL(target, window.location.origin).origin === window.location.origin;
    } catch (_error) {
      sameOrigin = false;
    }

    if (sameOrigin) {
      try {
        if (navigator.sendBeacon) {
          const blob = new Blob([json], { type: 'application/json' });
          if (navigator.sendBeacon(target, blob)) return;
        }
      } catch (_error) {
        // Fall through to fetch below.
      }
    }

    fetch(target, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: json,
      credentials: 'omit',
      keepalive: true
    }).catch(() => {});
  };

  const cleanPopularSearches = (items) => {
    const seen = new Set();
    const cleaned = [];
    for (const item of items || []) {
      const query = normalizeQuery(typeof item === 'string' ? item : item?.query);
      const key = query.toLowerCase();
      if (!isCleanSearchTerm(query) || seen.has(key)) continue;
      if (query.length < 3 && query.toLowerCase() !== 'lv') continue;
      seen.add(key);
      cleaned.push(query);
    }
    return cleaned.slice(0, 6);
  };

  const readPopularSearches = () => {
    try {
      const payload = JSON.parse(window.localStorage.getItem(popularSearchesKey) || 'null');
      if (!payload || Date.now() > Number(payload.expiresAt || 0)) return [];
      return cleanPopularSearches(payload.searches);
    } catch (_error) {
      return [];
    }
  };

  const writePopularSearches = (searches) => {
    try {
      window.localStorage.setItem(popularSearchesKey, JSON.stringify({
        searches: cleanPopularSearches(searches),
        expiresAt: Date.now() + 10 * 60 * 1000
      }));
    } catch (_error) {
      // Best-effort cache only.
    }
  };

  loadCustomerPrivacyApi();
  document.documentElement.classList.toggle('gsap-ready', Boolean(window.gsap));

  on('change', '[data-product-variant-select]', (_event, select) => {
    const formId = select.dataset.formId;
    const form = formId ? document.getElementById(formId) : select.closest('[data-product-shell]')?.querySelector('form');
    const idInput = form?.querySelector('[name="id"]');
    const selected = select.selectedOptions[0];
    const addButton = form?.querySelector('[data-add-to-cart]');
    const priceTarget = document.querySelector(select.dataset.priceTarget || '');

    if (idInput) idInput.value = select.value;
    if (priceTarget && selected?.dataset.price) priceTarget.textContent = selected.dataset.price;

    if (addButton && selected) {
      const available = selected.dataset.available === 'true';
      addButton.disabled = !available;
      addButton.textContent = available ? addButton.dataset.availableText : addButton.dataset.soldOutText;
      if (selected.dataset.priceAmount) addButton.dataset.productPrice = selected.dataset.priceAmount;
    }

    document.dispatchEvent(new CustomEvent('theme:variant-change', {
      detail: { variantId: select.value }
    }));
  });

  on('click', '[data-add-to-cart]', (_event, button) => {
    // "In your bag" opens the bag (assets/cart.js); it adds nothing.
    if (button.disabled || button.hasAttribute('data-in-bag')) return;
    const shell = button.closest('[data-product-shell]');
    trackSearchEvent('add_to_cart', {
      source: 'product_page',
      trigger: 'product_form',
      product_handle: button.dataset.productHandle || shell?.dataset.productHandle || '',
      product_title: button.dataset.productTitle || shell?.dataset.productTitle || '',
      product_url: button.dataset.productUrl || window.location.pathname,
      product_price: button.dataset.productPrice || shell?.dataset.productPrice || ''
    });
  });

  // The bag drawer (open, add, remove, terms) lives in assets/cart.js.

  const syncHeaderHeight = () => {
    const header = document.querySelector('[data-site-header]');
    if (!header) return;
    header.style.setProperty('--site-header-height', `${Math.round(header.getBoundingClientRect().height)}px`);
  };

  const animateSearchModalOpen = (modal) => {
    const gsap = activeGsap();
    const content = modal.querySelector('.site-header__search-modal-content');
    if (!gsap || !content) return false;
    gsap.killTweensOf([modal, content]);
    gsap.set(modal, { autoAlpha: 0 });
    gsap.set(content, { autoAlpha: 0, y: -12 });
    gsap.to(modal, { autoAlpha: 1, duration: 0.16, ease: 'power2.out' });
    gsap.to(content, { autoAlpha: 1, y: 0, duration: 0.28, ease: 'power3.out' });
    return true;
  };

  const focusSearchInput = (modal) => {
    const input = modal.querySelector('[data-predictive-input], input[type="search"]');
    if (!input) return;
    input.focus({ preventScroll: true });
    if (typeof input.setSelectionRange === 'function') {
      const end = input.value.length;
      input.setSelectionRange(end, end);
    }
  };

  let searchLockScrollY = 0;
  let searchLockBodyStyles = null;

  const setSearchPageLock = (locked) => {
    const root = document.documentElement;
    const body = document.body;
    if (!root || !body) return;

    if (locked) {
      if (body.classList.contains('search-lock')) return;
      searchLockScrollY = window.scrollY || root.scrollTop || 0;
      searchLockBodyStyles = {
        position: body.style.position,
        top: body.style.top,
        width: body.style.width,
        overflow: body.style.overflow
      };
      root.classList.add('search-lock');
      body.classList.add('search-lock');
      body.style.position = 'fixed';
      body.style.top = `-${searchLockScrollY}px`;
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      return;
    }

    if (!body.classList.contains('search-lock')) return;
    root.classList.remove('search-lock');
    body.classList.remove('search-lock');
    body.style.position = searchLockBodyStyles?.position || '';
    body.style.top = searchLockBodyStyles?.top || '';
    body.style.width = searchLockBodyStyles?.width || '';
    body.style.overflow = searchLockBodyStyles?.overflow || '';
    window.scrollTo(0, searchLockScrollY);
    searchLockBodyStyles = null;
    searchLockScrollY = 0;
  };

  const animateSearchModalClose = (modal, onComplete) => {
    const gsap = activeGsap();
    const content = modal.querySelector('.site-header__search-modal-content');
    if (!gsap || !content) return false;
    gsap.killTweensOf([modal, content]);
    gsap.to(content, { autoAlpha: 0, y: -8, duration: 0.14, ease: 'power2.in' });
    gsap.to(modal, {
      autoAlpha: 0,
      duration: 0.18,
      ease: 'power2.in',
      onComplete
    });
    return true;
  };

  const setHeaderDrawer = (open) => {
    const header = document.querySelector('[data-site-header]');
    const drawer = document.querySelector('[data-header-drawer]');
    const backdrop = document.querySelector('.site-header__drawer-backdrop');
    const trigger = document.querySelector('[data-header-drawer-open]');
    if (!drawer) return;
    // Escape anywhere on the page calls this with false: only hand focus back
    // to the menu button when the menu was actually open.
    const wasOpen = drawer.classList.contains('is-open');

    syncHeaderHeight();
    header?.classList.toggle('is-drawer-open', open);
    drawer.classList.toggle('is-open', open);
    backdrop?.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    trigger?.setAttribute('aria-expanded', String(open));

    if (open) {
      drawer.querySelector('a, summary, button')?.focus();
    } else if (wasOpen) {
      trigger?.focus();
    }
  };

  const setSearchModal = (open) => {
    const header = document.querySelector('[data-site-header]');
    const modal = document.querySelector('[data-search-modal]');
    const triggers = document.querySelectorAll('[data-search-modal-open]');
    if (!modal) return;

    if (open) {
      syncHeaderHeight();
      setHeaderDrawer(false);
      closeMegaMenus();
      setSearchPageLock(true);
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      header?.classList.add('is-search-open');
      triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
      focusSearchInput(modal);
      animateSearchModalOpen(modal);
      modal.querySelectorAll('predictive-search').forEach((search) => {
        if (search.input?.value.trim() && typeof search.search === 'function') {
          search.search();
        } else if (typeof search.showDefault === 'function') {
          search.showDefault();
        }
      });
      requestAnimationFrame(() => focusSearchInput(modal));
      window.setTimeout(() => focusSearchInput(modal), 90);
      window.setTimeout(() => focusSearchInput(modal), 260);
      return;
    }

    modal.setAttribute('aria-hidden', 'true');
    header?.classList.remove('is-search-open');
    triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    const finishClose = () => {
      modal.hidden = true;
      window.gsap?.set(modal, { clearProps: 'opacity,visibility' });
      window.gsap?.set(modal.querySelector('.site-header__search-modal-content'), { clearProps: 'opacity,visibility,transform' });
      modal.querySelectorAll('predictive-search').forEach((search) => {
        if (typeof search.hidePanels === 'function') search.hidePanels();
      });
      setSearchPageLock(false);
    };

    if (!animateSearchModalClose(modal, finishClose)) finishClose();
  };

  syncHeaderHeight();
  window.addEventListener('resize', syncHeaderHeight);

  on('click', '[data-header-drawer-open]', () => {
    setSearchModal(false);
    const drawer = document.querySelector('[data-header-drawer]');
    setHeaderDrawer(!drawer?.classList.contains('is-open'));
  });

  on('click', '[data-header-drawer-close]', () => {
    setHeaderDrawer(false);
  });

  on('click', '[data-search-modal-open]', () => {
    setSearchModal(true);
  });

  on('click', '[data-search-modal-close]', () => {
    setSearchModal(false);
  });

  const desktopMedia = window.matchMedia('(min-width: 1165px)');
  const closeMegaMenus = (except) => {
    document.querySelectorAll('.site-header__mega[open]').forEach((details) => {
      if (details === except) return;
      details.removeAttribute('open');
      details.classList.remove('is-open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  };

  document.querySelectorAll('.site-header__mega').forEach((details) => {
    const summary = details.querySelector('summary');
    let closeTimer;

    const open = () => {
      if (!desktopMedia.matches) return;
      clearTimeout(closeTimer);
      closeMegaMenus(details);
      details.setAttribute('open', '');
      details.classList.add('is-open');
      summary?.setAttribute('aria-expanded', 'true');
    };

    const close = () => {
      if (!desktopMedia.matches) return;
      closeTimer = setTimeout(() => {
        details.removeAttribute('open');
        details.classList.remove('is-open');
        summary?.setAttribute('aria-expanded', 'false');
      }, 120);
    };

    details.addEventListener('mouseenter', open);
    details.addEventListener('pointerenter', open);
    details.addEventListener('mouseover', open);
    details.addEventListener('mouseleave', close);
    details.addEventListener('pointerleave', close);
    details.addEventListener('focusin', open);
    details.addEventListener('focusout', (event) => {
      if (details.contains(event.relatedTarget)) return;
      close();
    });
  });

  document.addEventListener('mousemove', (event) => {
    if (!desktopMedia.matches) return;
    const details = event.target.closest('.site-header__mega');
    if (!details || details.open) return;
    closeMegaMenus(details);
    details.setAttribute('open', '');
    details.classList.add('is-open');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('.site-header__mega')) return;
    closeMegaMenus();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    setSearchModal(false);
    setHeaderDrawer(false);
    // The bag drawer closes itself (assets/cart.js).
  });

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', class PredictiveSearch extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[data-predictive-input]');
        this.results = this.querySelector('[data-predictive-results]');
        this.defaultPanel = this.querySelector('[data-search-default]');
        this.form = this.querySelector('form');
        this.loading = this.querySelector('[data-search-loading]');
        this.clearButton = this.querySelector('[data-search-clear]');
        this.endpoint = this.dataset.endpoint || luxeSearchEndpoint;
        this.eventsEndpoint = analyticsEndpointFor(this.endpoint);
        this.popularSearchesEndpoint = popularSearchesEndpointFor(this.endpoint);
        this.popularSearches = readPopularSearches();
        this.popularSearchesLoaded = Boolean(this.popularSearches.length);
        this.currentSearchId = '';
        this.abortController = null;
        this.intentTimer = null;
        this.lastIntentKey = '';
        this.lastIntentAt = 0;
        this.lastPayload = null;
        this.lastTerm = '';
        if (!this.input || !this.results) return;

        this.input.addEventListener('input', () => {
          this.updateClearButton();
          this.queueSearch();
        });
        this.input.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            setSearchModal(false);
          }
        });
        this.form?.addEventListener('submit', () => {
          const query = this.input.value;
          clearTimeout(this.intentTimer);
          this.saveRecentSearch(query);
          this.track('search_submit', {
            query,
            trigger: 'predictive_submit',
            search_id: this.currentSearchId
          });
        });
        this.form?.addEventListener('reset', () => {
          setTimeout(() => {
            this.input.value = '';
            this.updateClearButton();
            this.showDefault();
            this.input.focus();
          });
        });
        this.defaultPanel?.addEventListener('click', (event) => this.handleDefaultClick(event));
        this.results?.addEventListener('click', (event) => this.handleResultsClick(event));

        this.updateClearButton();
        // With the search overlay in charge (snippets/search-overlay-early.liquid), this
        // modal only opens as a fallback: build its panel and fetch popular searches when
        // it is about to open, not on every page view.
        if (document.documentElement.classList.contains('has-search-overlay')) {
          const prepare = () => {
            if (this.prepared) return;
            this.prepared = true;
            this.renderDefault();
            this.loadPopularSearches();
          };
          this.addEventListener('focusin', prepare);
          document.addEventListener('click', (event) => {
            if (event.target.closest?.('[data-search-modal-open]')) prepare();
          });
        } else {
          this.renderDefault();
          this.loadPopularSearches();
        }
      }

      setLoading(loading) {
        if (this.loading) this.loading.hidden = !loading;
      }

      updateClearButton() {
        if (this.clearButton) this.clearButton.hidden = !this.input.value.trim();
      }

      queueSearch() {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), 180);
      }

      async search() {
        const term = normalizeQuery(this.input.value);
        if (!term) {
          clearTimeout(this.intentTimer);
          this.showDefault();
          return;
        }

        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();
        const url = new URL(this.endpoint, window.location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('per_page', '20');
        url.searchParams.set('analytics', 'false');
        this.currentSearchId = makeAnalyticsId('search');
        appendSearchAnalyticsParams(url, {
          source: 'predictive_search',
          trigger: 'predictive_typeahead',
          search_id: this.currentSearchId
        });

        try {
          this.setLoading(true);
          const response = await fetch(url, {
            signal: this.abortController.signal,
            headers: { Accept: 'application/json' }
          });
          const payload = await response.json();
          if (!response.ok || payload.status === 'error') {
            throw new Error(payload.message || `Search failed: ${response.status}`);
          }
          this.lastPayload = payload;
          this.lastTerm = term;
          this.results.innerHTML = this.renderSuggestions(payload, term);
          this.open();
          this.animatePanel(this.results);
          this.queueSearchIntent(term, payload);
        } catch (_error) {
          if (_error.name === 'AbortError') return;
          this.results.innerHTML = this.renderFallback(term);
          this.open();
          this.animatePanel(this.results);
        } finally {
          this.setLoading(false);
        }
      }

      open() {
        if (this.defaultPanel) this.defaultPanel.hidden = true;
        this.results.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
      }

      showDefault() {
        this.results.hidden = true;
        this.results.innerHTML = '';
        this.renderDefault();
        this.loadPopularSearches();
        if (this.defaultPanel) {
          this.defaultPanel.hidden = false;
          this.input.setAttribute('aria-expanded', 'true');
          this.animatePanel(this.defaultPanel);
        } else {
          this.input.setAttribute('aria-expanded', 'false');
        }
      }

      hidePanels() {
        this.results.hidden = true;
        if (this.defaultPanel) this.defaultPanel.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
      }

      close() {
        this.hidePanels();
      }

      animatePanel(root) {
        const gsap = activeGsap();
        if (!gsap || !root || root.hidden) return;
        const items = root.querySelectorAll('.predictive-search__recent-row, .predictive-search__suggestion-item, .predictive-search__product-strip .aa-Item, .predictive-search__quick-products .aa-Item');
        if (!items.length) return;
        gsap.killTweensOf(items);
        gsap.fromTo(
          items,
          { autoAlpha: 0, y: 8 },
          { autoAlpha: 1, y: 0, duration: 0.24, stagger: 0.035, ease: 'power2.out', clearProps: 'opacity,visibility,transform' }
        );
      }

      handleDefaultClick(event) {
        const removeSearch = event.target.closest('[data-recent-search-remove]');
        if (removeSearch) {
          event.preventDefault();
          this.removeRecentSearch(removeSearch.dataset.recentSearchRemove);
          return;
        }

        if (event.target.closest('[data-recent-search-clear]')) {
          event.preventDefault();
          writeStoredList(recentSearchesKey, []);
          this.renderDefault();
          return;
        }

        if (event.target.closest('[data-recent-products-clear]')) {
          event.preventDefault();
          writeStoredList(recentProductsKey, []);
          this.renderDefault();
          return;
        }

        const recentSearch = event.target.closest('[data-recent-search]');
        if (recentSearch) {
          this.saveRecentSearch(recentSearch.dataset.recentSearch);
          this.track('suggestion_click', {
            query: recentSearch.dataset.recentSearch,
            trigger: 'recent_search'
          });
        }
      }

      handleResultsClick(event) {
        const suggestion = event.target.closest('[data-search-suggestion]');
        if (!suggestion) return;
        clearTimeout(this.intentTimer);
        this.saveRecentSearch(suggestion.dataset.searchSuggestion);
        this.track('suggestion_click', {
          query: suggestion.dataset.searchSuggestion,
          trigger: 'predictive_suggestion',
          search_id: this.currentSearchId,
          metadata: {
            typedQuery: this.input.value
          }
        });
      }

      queueSearchIntent(term, payload) {
        clearTimeout(this.intentTimer);
        this.intentTimer = setTimeout(() => {
          this.trackSearchIntent(term, payload, 'predictive_settled');
        }, searchIntentDelay);
      }

      trackSearchIntent(term, payload, trigger = 'predictive_settled') {
        const query = normalizeQuery(term);
        if (!shouldTrackSearchIntent(query)) return;
        const resultCount = resultCountFor(payload);
        const intentKey = [
          searchIntentTypeFor(payload),
          query.toLowerCase(),
          resultCount ?? ''
        ].join(':');
        const now = Date.now();
        if (this.lastIntentKey === intentKey && now - this.lastIntentAt < searchIntentDedupeMs) return;

        this.lastIntentKey = intentKey;
        this.lastIntentAt = now;
        this.track(searchIntentTypeFor(payload), {
          query,
          trigger,
          result_count: resultCount,
          per_page: payload?.query?.perPage,
          sort: payload?.query?.sort,
          search_time_ms: payload?.searchTimeMs,
          search_id: this.currentSearchId
        });
      }

      track(type, payload = {}) {
        trackSearchEvent(type, {
          source: 'predictive_search',
          ...payload
        }, this.eventsEndpoint);
      }

      async loadPopularSearches() {
        if (this.popularSearchesLoaded) return;
        this.popularSearchesLoaded = true;

        try {
          const url = new URL(this.popularSearchesEndpoint, window.location.origin);
          url.searchParams.set('limit', '6');
          const shop = analyticsConfig().shop || '';
          if (shop) url.searchParams.set('shop', shop);
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          const payload = await response.json();
          const searches = cleanPopularSearches(payload?.searches);
          if (!response.ok || !searches.length) return;

          this.popularSearches = searches;
          writePopularSearches(searches);
          if (this.defaultPanel && !this.defaultPanel.hidden && cleanRecentSearches().length === 0) {
            this.renderDefault();
            this.animatePanel(this.defaultPanel);
          }
        } catch (_error) {
          // Fallback searches keep the modal useful when analytics is unavailable.
        }
      }

      saveRecentSearch(value) {
        const query = normalizeQuery(value);
        if (!isCleanSearchTerm(query)) return;
        const next = [
          query,
          ...cleanRecentSearches().filter((item) => item.toLowerCase() !== query.toLowerCase())
        ].slice(0, 6);
        writeStoredList(recentSearchesKey, next);
      }

      removeRecentSearch(value) {
        const query = normalizeQuery(value);
        const next = cleanRecentSearches().filter((item) => item.toLowerCase() !== query.toLowerCase());
        writeStoredList(recentSearchesKey, next);
        this.renderDefault();
        this.animatePanel(this.defaultPanel);
      }

      renderDefault() {
        if (!this.defaultPanel) return;
        const recentSearches = cleanRecentSearches().slice(0, 5);
        const recentProducts = cleanRecentProducts().slice(0, 8);
        const popularSearches = this.popularSearches.length ? this.popularSearches : fallbackPopularSearches;
        const searches = recentSearches.length ? recentSearches : popularSearches;
        const searchHeading = recentSearches.length ? 'Recently Searched' : 'Popular Searches';

        this.defaultPanel.innerHTML = `
          <div class="aa-PanelLayout predictive-search__panel predictive-search__panel--default">
            <section class="aa-Source predictive-search__section predictive-search__section--recent-searches">
              <div class="aa-SourceHeader predictive-search__header">
                <h4 class="predictive-search__heading">${searchHeading}</h4>
                ${recentSearches.length ? '<button type="button" class="predictive-search__clear-link" data-recent-search-clear>Clear</button>' : ''}
              </div>
              <ul class="aa-List predictive-search__list" role="list">
                ${searches.map((query) => `
                  <li class="aa-Item predictive-search__recent-row">
                    <a class="predictive-search__recent-link" href="${searchUrlFor(query)}" data-recent-search="${escapeHtml(query)}">${escapeHtml(query)}</a>
                    ${recentSearches.length ? `<button class="predictive-search__remove" type="button" aria-label="Delete ${escapeHtml(query)} from recent searches" data-recent-search-remove="${escapeHtml(query)}">&times;</button>` : `<span class="predictive-search__arrow" aria-hidden="true">${searchArrowIcon}</span>`}
                  </li>
                `).join('')}
              </ul>
            </section>
            ${recentProducts.length ? `
              <section class="aa-Source predictive-search__section predictive-search__section--recent-products">
                <div class="aa-SourceHeader predictive-search__header">
                  <h4 class="predictive-search__heading">Recently Viewed</h4>
                  <button type="button" class="predictive-search__clear-link" data-recent-products-clear>Clear</button>
                </div>
                <ul class="aa-List predictive-search__product-strip" role="list">
                  ${recentProducts.map((product) => `
                    <li class="aa-Item">
                      <a class="predictive-search__recent-product" href="${escapeHtml(product.url || '#')}" aria-label="${escapeHtml(product.title || 'View Product')}">
                        ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title || '')}" loading="lazy">` : ''}
                      </a>
                    </li>
                  `).join('')}
                </ul>
              </section>
            ` : ''}
          </div>
        `;
      }

      buildSuggestionList(payload, term) {
        const candidates = new Map();
        const termLower = normalizeQuery(term).toLowerCase();
        const termWords = termLower.match(/[a-z0-9]+/g) || [];
        const genericSuggestions = new Set(['accessories', 'fashion accessories', 'bags', 'shoes', 'jewelry', 'watches']);
        const stopWords = new Set(['with', 'and', 'the', 'bag', 'bags', 'mini', 'small', 'large', 'medium']);
        const modelWords = new Set(['arcon', 'birkin', 'bolide', 'chaine', 'constance', 'evelyne', 'garden', 'halzan', 'herbag', 'jypsiere', 'kelly', 'lindy', 'mini', 'picotin', 'roulis', 'verrou', 'wallet']);
        const attributeWords = new Set(['black', 'blue', 'brown', 'chevre', 'crocodile', 'epsom', 'gold', 'green', 'lambskin', 'leather', 'medium', 'mysore', 'noir', 'pale', 'palladium', 'pink', 'silver', 'small', 'sylvestre', 'togo', 'white']);
        const termMatchesQuery = (query) => {
          if (!termLower) return true;
          if (query.includes(termLower) || termLower.includes(query)) return true;
          const queryWords = query.match(/[a-z0-9]+/g) || [];
          return termWords.every((word, index) => queryWords[index]?.startsWith(word));
        };
        const add = (value, weight = 0, force = false) => {
          const query = normalizeQuery(value).toLowerCase();
          if (!query) return;
          if (!force && (isTaxonomyTerm(query) || query.length > 34)) return;
          if (!force && termLower.length <= 1 && genericSuggestions.has(query)) return;
          if (!force && !termMatchesQuery(query)) return;
          const existing = candidates.get(query);
          if (!existing || weight > existing.weight) {
            candidates.set(query, { query, weight });
          }
        };
        const productWords = (title) => normalizeQuery(title).toLowerCase().match(/[a-z0-9]+/g) || [];
        const addTitlePhrases = (title) => {
          const words = productWords(title);
          if (!words.length || !termWords.length) return;
          const matchesPrefix = termWords.every((word, index) => words[index]?.startsWith(word));
          if (matchesPrefix) {
            const startLength = Math.max(termWords.length, 2);
            for (let length = startLength; length <= Math.min(words.length, 4); length += 1) {
              add(words.slice(0, length).join(' '), 85 - length, true);
            }
          }

          if (termWords.length < 2 || !words[0]?.startsWith(termWords[0])) return;
          const partial = termWords[termWords.length - 1];
          for (let index = 1; index < words.length; index += 1) {
            const word = words[index];
            if (!word.startsWith(partial) || !modelWords.has(word)) continue;
            add(`${words[0]} ${word}`, 92, true);
            const nextModel = words.slice(index + 1).find((nextWord) => (
              modelWords.has(nextWord)
              || (!attributeWords.has(nextWord) && !/^\d+$/.test(nextWord) && nextWord.length > 3)
            ));
            if (nextModel) add(`${words[0]} ${word} ${nextModel}`, 89, true);
          }

          const startIndex = words.findIndex((word, index) => index > 0 && word.startsWith(partial) && !attributeWords.has(word));
          if (startIndex < 1) return;
          for (let length = startIndex + 1; length <= Math.min(words.length, startIndex + 3); length += 1) {
            add(words.slice(0, length).join(' '), 68 - length, true);
          }
        };
        const addTitleTerms = (title) => {
          const words = productWords(title);
          for (const word of words) {
            if (word.length < 4) continue;
            if (stopWords.has(word)) continue;
            add(word, 10);
          }
        };

        if (termLower.length > 1) add(term, 100, true);
        for (const product of payload.hits || []) {
          addTitlePhrases(product.title);
        }
        for (const facet of payload.facets || []) {
          const param = facet.param || facet.field || '';
          if (!['designer', 'brand', 'collection_title', 'type', 'color', 'material', 'hardware'].includes(param)) continue;
          for (const item of facet.values || []) add(item.value, 36);
        }
        for (const product of payload.hits || []) {
          add(product.designer || product.brand || product.vendor, 28);
          addTitleTerms(product.title);
        }

        return Array.from(candidates.values())
          .filter((item) => isCleanSearchTerm(item.query))
          .sort((a, b) => b.weight - a.weight || a.query.length - b.query.length)
          .map((item) => item.query)
          .slice(0, 6);
      }

      renderSuggestions(payload, term) {
        const suggestions = this.buildSuggestionList(payload, term);
        const products = (payload.hits || []).filter((product) => product?.title && product?.url).slice(0, 4);
        return `
          <div class="aa-PanelLayout predictive-search__panel predictive-search__panel--suggestions${products.length ? ' predictive-search__panel--with-products' : ''}">
            <section class="aa-Source predictive-search__section predictive-search__section--suggestions">
              <ul class="aa-List predictive-search__suggestion-list" role="listbox" aria-label="Search suggestions">
                ${suggestions.map((query) => `
                  <li class="aa-Item predictive-search__suggestion-item" role="option">
                    <a class="predictive-search__suggestion-link" href="${searchUrlFor(query)}" data-search-suggestion="${escapeHtml(query)}">
                      <span>${escapeHtml(query)}</span>
                      <span class="predictive-search__arrow" aria-hidden="true">${searchArrowIcon}</span>
                    </a>
                  </li>
                `).join('')}
              </ul>
            </section>
            ${products.length ? `
              <section class="aa-Source predictive-search__section predictive-search__section--quick-products">
                <div class="aa-SourceHeader predictive-search__header">
                  <h4 class="predictive-search__heading">Top Matches</h4>
                </div>
                <ul class="aa-List predictive-search__quick-products" role="list" aria-label="Top matching products">
                  ${products.map((product, index) => this.renderQuickProduct(product, index)).join('')}
                </ul>
              </section>
            ` : ''}
          </div>
        `;
      }

      renderQuickProduct(product, index) {
        const price = formatPrice(product.price_min || product.price);
        const rawPrice = product.price_min || product.price || '';
        const title = product.title || '';
        const rawDesigner = product.designer || product.brand || product.vendor || '';
        const designer = rawDesigner && !title.toLowerCase().startsWith(rawDesigner.toLowerCase()) ? rawDesigner : '';
        const meta = [designer, product.color, product.condition]
          .filter(Boolean)
          .filter((value, itemIndex, items) => items.indexOf(value) === itemIndex)
          .join(' / ');
        return `
          <li class="aa-Item">
            <a
              class="predictive-search__quick-product"
              href="${escapeHtml(product.url || '#')}"
              data-search-product-link
              data-search-source="predictive_search"
              data-search-id="${escapeHtml(this.currentSearchId)}"
              data-search-query="${escapeHtml(this.input?.value || '')}"
              data-product-handle="${escapeHtml(product.handle || '')}"
              data-product-title="${escapeHtml(title)}"
              data-product-url="${escapeHtml(product.url || '')}"
              data-product-position="${index + 1}"
              data-product-price="${escapeHtml(rawPrice)}"
            >
              <span class="predictive-search__quick-product-image">
                ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(title)}" loading="lazy">` : ''}
              </span>
              <span class="predictive-search__quick-product-content">
                <span class="predictive-search__quick-product-title">${escapeHtml(title)}</span>
                ${meta ? `<span class="predictive-search__quick-product-meta">${escapeHtml(meta)}</span>` : ''}
                ${price ? `<span class="predictive-search__price">${price}</span>` : ''}
              </span>
              <span class="predictive-search__quick-product-arrow" aria-hidden="true">${searchArrowIcon}</span>
            </a>
          </li>
        `;
      }

      renderFallback(term) {
        return `
          <div class="aa-PanelLayout predictive-search__panel predictive-search__panel--suggestions">
            <ul class="aa-List predictive-search__suggestion-list" role="listbox" aria-label="Search suggestions">
              <li class="aa-Item predictive-search__suggestion-item" role="option">
                <a class="predictive-search__suggestion-link" href="${searchUrlFor(term)}" data-search-suggestion="${escapeHtml(term)}">
                  <span>search for ${escapeHtml(term)}</span>
                  <span class="predictive-search__arrow" aria-hidden="true">${searchArrowIcon}</span>
                </a>
              </li>
            </ul>
          </div>
        `;
      }
    });
  }

  const saveRecentProduct = (product) => {
    const title = normalizeQuery(product?.title);
    const url = normalizeProductUrl(product?.url);
    if (!title || !url) return;
    const image = product.image || '';
    const next = [
      { title, url, image },
      ...cleanRecentProducts().filter((item) => normalizeProductUrl(item.url) !== url)
    ].slice(0, 8);
    writeStoredList(recentProductsKey, next);
  };

  const productJson = document.querySelector('[data-recent-product-json]');
  if (productJson) {
    try {
      saveRecentProduct(JSON.parse(productJson.textContent || '{}'));
    } catch (_error) {
      // Ignore malformed theme-editor placeholder JSON.
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initProductCardGalleries(document);
      initCollectionRailImages(document);
    });
  } else {
    initProductCardGalleries(document);
    initCollectionRailImages(document);
  }

  on('click', '[data-product-card] a[href]', (_event, link) => {
    const card = link.closest('[data-product-card]');
    const productLink = card?.querySelector('h2 a[href]') || link;
    const image = card?.querySelector('img');
    saveRecentProduct({
      title: productLink?.textContent || productLink?.getAttribute('aria-label') || '',
      url: productLink?.getAttribute('href') || '',
      image: image?.currentSrc || image?.src || ''
    });
  });

  on('click', '[data-search-product-link]', (_event, link) => {
    const searchRoot = link.closest('predictive-search, luxe-search');
    trackSearchEvent('product_click', {
      source: link.dataset.searchSource || 'storefront_search',
      trigger: 'product_link',
      search_id: link.dataset.searchId || '',
      query: link.dataset.searchQuery || '',
      product_handle: link.dataset.productHandle || '',
      product_title: link.dataset.productTitle || link.textContent || '',
      product_url: link.dataset.productUrl || link.getAttribute('href') || '',
      product_position: link.dataset.productPosition || '',
      product_price: link.dataset.productPrice || ''
    }, searchRoot?.eventsEndpoint || analyticsEndpointFor(searchRoot?.dataset.endpoint));
  });

  const luxeFilterParams = [
    'designer',
    'collection_title',
    'category',
    'type',
    'condition',
    'color',
    'material',
    'hardware',
    'includes',
    'location',
    'availability'
  ];
  const luxePriceRanges = {
    Under1000: { label: 'Under $1,000', max: '1000' },
    From1000To2000: { label: '$1,000 - $2,000', min: '1000', max: '2000' },
    From2000To5000: { label: '$2,000 - $5,000', min: '2000', max: '5000' },
    From5000To8000: { label: '$5,000 - $8,000', min: '5000', max: '8000' },
    Over8000: { label: '$8,000+', min: '8000' }
  };
  const luxeFacetCollapseLimit = 7;
  // Filter groups left out of the panel (LDJ, 9 Oct 2026): Type, Material, Hardware, Includes.
  const luxeHiddenFacets = ['type', 'material', 'hardware', 'includes'];
  const formatFacetLabel = (value) => {
    if (!value) return '';
    if (luxePriceRanges[value]) return luxePriceRanges[value].label;
    return String(value).replace(/_/g, ' ');
  };

  if (!customElements.get('luxe-search')) {
    customElements.define('luxe-search', class LuxeSearch extends HTMLElement {
      connectedCallback() {
        this.endpoint = this.dataset.endpoint;
        this.eventsEndpoint = analyticsEndpointFor(this.endpoint);
        this.mode = this.dataset.mode || 'search';
        this.collection = this.dataset.collection || '';
        this.collectionTitle = this.dataset.collectionTitle || '';
        this.basePerPage = Number(this.dataset.perPage) || 24;
        this.mobilePerPage = Math.max(12, Number(this.dataset.mobilePerPage) || 50);
        this.maxMobileRendered = Math.max(this.mobilePerPage * 2, Number(this.dataset.maxMobileRendered) || 350);
        this.perPage = this.effectivePerPage();
        this.form = this.querySelector('[data-search-form]');
        this.input = this.querySelector('[data-search-input]');
        this.sort = this.querySelector('[data-search-sort]');
        this.count = this.querySelector('[data-search-count]');
        this.facets = this.querySelector('[data-facets]');
        this.results = this.querySelector('[data-search-results]');
        this.status = this.querySelector('[data-search-status]');
        this.pagination = this.querySelector('[data-search-pagination]');
        this.sentinel = this.querySelector('[data-search-sentinel]');
        this.activeFilters = this.querySelector('[data-active-filters]');
        this.filterPanel = this.querySelector('[data-filter-panel]');
        this.filterToggle = this.querySelector('[data-filter-toggle]');
        this.filterApply = this.querySelector('[data-filter-apply]');
        this.filterClear = this.querySelector('[data-filter-clear]');
        this.filterCountBadges = Array.from(this.querySelectorAll('[data-filter-count]'));
        this.enableInfinite = this.dataset.infinite === 'true';
        this.abortController = null;
        this.state = this.stateFromUrl();
        this.currentSearchId = '';
        this.intentTimer = null;
        this.lastIntentKey = '';
        this.lastIntentAt = 0;
        this.lastPayload = null;
        this.totalFound = 0;
        this.hasMore = false;
        this.loadingMore = false;
        this.loadMoreToken = '';
        this.renderedProductKeys = new Set();
        this.infiniteObserver = null;

        if (!this.endpoint || !this.results) return;

        if (this.input) this.input.value = this.state.q === '*' ? '' : this.state.q;
        if (this.sort) this.sort.value = this.state.sort;
        this.updateFilterControls();

        this.form?.addEventListener('submit', (event) => {
          event.preventDefault();
          this.state.q = this.input?.value.trim() || '*';
          this.state.page = 1;
          this.track('search_submit', {
            query: this.state.q,
            trigger: 'search_page_submit'
          });
          this.search(true, 'search_page_submit');
        });

        this.input?.addEventListener('input', () => {
          clearTimeout(this.inputTimer);
          this.inputTimer = setTimeout(() => {
            this.state.q = this.input.value.trim() || '*';
            this.state.page = 1;
            this.search(true, 'search_page_typeahead');
          }, 300);
        });

        this.sort?.addEventListener('change', () => {
          this.state.sort = this.sort.value || 'relevance';
          this.state.page = 1;
          this.track('filter_click', {
            query: this.state.q,
            trigger: 'sort_change',
            sort: this.state.sort
          });
          this.search(true, 'sort_change');
        });

        this.facets?.addEventListener('change', (event) => {
          const input = event.target.closest('[data-filter-input]');
          if (!input) return;
          this.normalizeExclusiveFilterInput(input);

          if (this.isMobileFilterMode() && this.filterPanel?.classList.contains('is-open')) {
            this.facetDraftDirty = true;
            this.updateFilterControls({ draft: true });
            return;
          }

          this.toggleFilter(input);
          this.state.page = 1;
          this.track('filter_click', {
            query: this.state.q,
            trigger: input.checked ? 'filter_add' : 'filter_remove',
            filters: {
              [input.dataset.filterParam || 'filter']: input.dataset.filterValue || ''
            },
            metadata: {
              checked: input.checked
            }
          });
          this.search(true, input.checked ? 'filter_add' : 'filter_remove');
        });

        this.facets?.addEventListener('click', (event) => {
          const button = event.target.closest('[data-facet-more]');
          if (!button) return;
          event.preventDefault();
          this.toggleFacetMore(button);
        });

        this.activeFilters?.addEventListener('click', (event) => {
          const button = event.target.closest('[data-remove-filter]');
          if (!button) return;
          this.removeFilter(button.dataset.removeFilter, button.dataset.removeValue);
          this.state.page = 1;
          this.track('filter_click', {
            query: this.state.q,
            trigger: 'filter_chip_remove',
            filters: {
              [button.dataset.removeFilter || 'filter']: button.dataset.removeValue || ''
            }
          });
          this.search(true, 'filter_chip_remove');
        });

        this.pagination?.addEventListener('click', (event) => {
          const button = event.target.closest('[data-page]');
          if (!button) return;
          this.state.page = Number(button.dataset.page) || 1;
          this.search(true, 'pagination');
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });

        this.filterToggle?.addEventListener('click', () => {
          const isOpen = this.filterPanel?.classList.contains('is-open');
          this.setFiltersOpen(this.isMobileFilterMode() ? true : !isOpen);
        });
        this.querySelector('[data-filter-close]')?.addEventListener('click', () => this.setFiltersOpen(false));
        this.filterApply?.addEventListener('click', () => this.applyDraftFilters());
        this.filterClear?.addEventListener('click', () => this.clearDraftFilters());

        window.addEventListener('popstate', () => {
          this.state = this.stateFromUrl();
          if (this.input) this.input.value = this.state.q === '*' ? '' : this.state.q;
          if (this.sort) this.sort.value = this.state.sort;
          this.updateFilterControls();
          this.search(false, 'popstate');
        });

        initProductCardGalleries(this);
        this.setupInfiniteScroll();
        this.search(false, 'search_page_load');
      }

      stateFromUrl() {
        const params = new URLSearchParams(window.location.search);
        const filters = {};

        for (const param of luxeFilterParams) {
          const values = params.getAll(param).flatMap((value) => value.split(',')).filter(Boolean);
          if (values.length) filters[param] = new Set(values);
        }

        return {
          q: params.get('q') || this.dataset.initialQuery || '*',
          page: Number(params.get('page')) || 1,
          sort: params.get('sort') || 'relevance',
          priceMin: params.get('price_min') || '',
          priceMax: params.get('price_max') || '',
          filters
        };
      }

      setFiltersOpen(open, options = {}) {
        if (!this.filterPanel) return;

        const { resetDraft = true } = options;
        const mobile = this.isMobileFilterMode();
        this.filterToggle?.setAttribute('aria-expanded', String(open));
        this.classList.toggle('is-filter-panel-open', open);

        if (open) {
          // The sheet already shows the applied filters: a discarded draft is put back when it
          // closes, and every search re-renders the groups. Rebuilding ~900 nodes here delayed
          // the sheet and started a second stagger over the first.
          if (mobile && this.facetDraftDirty) this.restoreFacetState();
          document.body.classList.toggle('luxe-search-filters-open', mobile);
          document.documentElement.classList.toggle('luxe-search-filters-open', mobile);
          this.filterPanel.classList.add('is-open');
          this.updateFilterControls({ draft: mobile });
          this.animateFilterFacets();
          return;
        }

        const finishClose = () => {
          document.body.classList.remove('luxe-search-filters-open');
          document.documentElement.classList.remove('luxe-search-filters-open');
          if (mobile && resetDraft && this.facetDraftDirty) this.restoreFacetState();
          this.updateFilterControls();
        };

        if (mobile && this.filterPanel.classList.contains('is-open')) {
          this.filterPanel.classList.remove('is-open');
          window.setTimeout(finishClose, 280);
        } else {
          this.filterPanel.classList.remove('is-open');
          finishClose();
        }
      }

      isMobileFilterMode() {
        return window.matchMedia('(max-width: 989px)').matches;
      }

      effectivePerPage() {
        if (!this.isMobileFilterMode()) return this.basePerPage;
        return Math.min(this.basePerPage, this.mobilePerPage);
      }

      refreshPerPage() {
        this.perPage = this.effectivePerPage();
        return this.perPage;
      }

      normalizeExclusiveFilterInput(input) {
        if (input.dataset.filterParam !== 'price' || !input.checked || !this.facets) return;
        this.facets.querySelectorAll('[data-filter-input][data-filter-param="price"]').forEach((option) => {
          if (option !== input) option.checked = false;
        });
      }

      applyDraftFilters() {
        if (!this.facets) return;
        const filters = {};
        let priceMin = '';
        let priceMax = '';

        this.facets.querySelectorAll('[data-filter-input]:checked').forEach((input) => {
          const param = input.dataset.filterParam;
          const value = input.dataset.filterValue;
          if (!param || !value) return;

          if (param === 'price') {
            const range = luxePriceRanges[value] || {};
            priceMin = range.min || '';
            priceMax = range.max || '';
            return;
          }

          if (!filters[param]) filters[param] = new Set();
          filters[param].add(value);
        });

        this.state.filters = filters;
        this.state.priceMin = priceMin;
        this.state.priceMax = priceMax;
        this.state.page = 1;
        this.track('filter_click', {
          query: this.state.q,
          trigger: 'filter_apply',
          filters: this.analyticsFilters(),
          metadata: { mobile: true }
        });
        this.setFiltersOpen(false, { resetDraft: false });
        this.search(true, 'filter_apply');
      }

      clearDraftFilters() {
        if (!this.facets) return;
        if (this.isMobileFilterMode() && this.filterPanel?.classList.contains('is-open')) this.facetDraftDirty = true;
        this.facets.querySelectorAll('[data-filter-input]').forEach((input) => {
          input.checked = false;
        });
        this.updateFilterControls({ draft: this.isMobileFilterMode() && this.filterPanel?.classList.contains('is-open') });
      }

      toggleFacetMore(button) {
        const facet = button.closest('.luxe-search__facet');
        if (!facet) return;

        const expanded = button.getAttribute('aria-expanded') === 'true';
        const nextExpanded = !expanded;
        const extraOptions = Array.from(facet.querySelectorAll('[data-facet-extra]'));
        extraOptions.forEach((option) => {
          if (nextExpanded) {
            option.hidden = false;
          } else {
            option.hidden = true;
          }
        });

        facet.classList.toggle('is-expanded', nextExpanded);
        button.setAttribute('aria-expanded', String(nextExpanded));
        button.textContent = nextExpanded ? button.dataset.lessLabel || 'Show Less' : button.dataset.moreLabel || 'Show More';

        if (nextExpanded) animateIn(extraOptions, { duration: 0.16, stagger: 0.01, y: -4, limit: 50 });
      }

      restoreFacetState() {
        this.facetDraftDirty = false;
        if (this.lastPayload?.facets) {
          this.renderFacets(this.lastPayload.facets, { animate: false });
          return;
        }
        this.updateFilterControls();
      }

      activeFilterCount() {
        let count = 0;
        for (const values of Object.values(this.state.filters || {})) {
          count += values?.size || 0;
        }
        if (this.state.priceMin || this.state.priceMax) count += 1;
        return count;
      }

      draftFilterCount() {
        if (!this.facets) return this.activeFilterCount();
        let count = 0;
        let hasPrice = false;
        this.facets.querySelectorAll('[data-filter-input]:checked').forEach((input) => {
          if (input.dataset.filterParam === 'price') {
            hasPrice = true;
          } else {
            count += 1;
          }
        });
        return count + (hasPrice ? 1 : 0);
      }

      updateFilterControls(options = {}) {
        const count = options.draft ? this.draftFilterCount() : this.activeFilterCount();
        this.filterCountBadges?.forEach((badge) => {
          badge.textContent = String(count);
          badge.hidden = count === 0;
        });
        this.filterToggle?.classList.toggle('is-filtered', count > 0);
        if (this.filterClear) this.filterClear.disabled = count === 0;
        if (this.filterApply) this.filterApply.textContent = 'Show Products';
      }

      animateFilterFacets() {
        if (!this.facets) return;
        const items = Array.from(this.facets.querySelectorAll('.luxe-search__facet:not(.luxe-search__facet--skeleton):not([hidden])'));
        animateIn(items, { duration: 0.22, stagger: 0.018, y: 10, limit: 14 });
      }

      toggleFilter(input) {
        const param = input.dataset.filterParam;
        const value = input.dataset.filterValue;
        if (!param || !value) return;

        if (param === 'price') {
          const range = luxePriceRanges[value] || {};
          this.state.priceMin = input.checked ? range.min || '' : '';
          this.state.priceMax = input.checked ? range.max || '' : '';
          return;
        }

        if (!this.state.filters[param]) this.state.filters[param] = new Set();

        if (input.checked) {
          this.state.filters[param].add(value);
        } else {
          this.state.filters[param].delete(value);
          if (!this.state.filters[param].size) delete this.state.filters[param];
        }
      }

      removeFilter(param, value) {
        if (param === 'price') {
          this.state.priceMin = '';
          this.state.priceMax = '';
          return;
        }

        this.state.filters[param]?.delete(value);
        if (!this.state.filters[param]?.size) delete this.state.filters[param];
      }

      buildApiUrl() {
        this.refreshPerPage();
        const url = new URL(this.endpoint, window.location.origin);
        const q = this.state.q || '*';
        url.searchParams.set('q', q);
        url.searchParams.set('page', String(this.state.page));
        url.searchParams.set('per_page', String(this.perPage));
        url.searchParams.set('sort', this.state.sort || 'relevance');
        if (this.collection) url.searchParams.set('collection', this.collection);
        // A collection page lists its own collection's pieces, nothing else. The title "scope"
        // also matched every collection, designer, type or category with the same title, so
        // "Totes" under Prada listed Gucci, Fendi and Saint Laurent totes too (LDJ, 8 Oct 2026).
        if (this.collectionTitle && !this.collection) url.searchParams.set('collection_scope', this.collectionTitle);
        if (this.state.priceMin) url.searchParams.set('price_min', this.state.priceMin);
        if (this.state.priceMax) url.searchParams.set('price_max', this.state.priceMax);

        for (const [param, values] of Object.entries(this.state.filters)) {
          for (const value of values) {
            url.searchParams.append(param, value);
          }
        }

        return url;
      }

      buildPageUrl() {
        const url = new URL(window.location.href);
        const params = new URLSearchParams();
        if (this.state.q && this.state.q !== '*') params.set('q', this.state.q);
        if (this.state.page > 1) params.set('page', String(this.state.page));
        if (this.state.sort && this.state.sort !== 'relevance') params.set('sort', this.state.sort);
        if (this.state.priceMin) params.set('price_min', this.state.priceMin);
        if (this.state.priceMax) params.set('price_max', this.state.priceMax);

        for (const [param, values] of Object.entries(this.state.filters)) {
          for (const value of values) {
            params.append(param, value);
          }
        }

        url.search = params.toString();
        return url;
      }

      renderProductSkeleton() {
        if (!this.results) return;
        const count = Math.min(Math.max(6, this.perPage || 8), 12);
        this.renderedProductKeys = new Set();
        this.clearLoadMoreSkeletons();
        this.results.innerHTML = Array.from({ length: count }).map(() => `
          <article class="product-card product-card--skeleton" aria-hidden="true">
            <div class="product-card__frame">
              <span class="product-card__media luxe-skeleton product-card__skeleton-media"></span>
            </div>
            <div class="product-card__content">
              <div class="product-card__details product-card__skeleton-details">
                <span class="luxe-skeleton product-card__skeleton-line product-card__skeleton-line--brand"></span>
                <span class="luxe-skeleton product-card__skeleton-line"></span>
                <span class="luxe-skeleton product-card__skeleton-line product-card__skeleton-line--short"></span>
                <span class="luxe-skeleton product-card__skeleton-line product-card__skeleton-line--price"></span>
              </div>
            </div>
          </article>
        `).join('');
        this.animateProductCards(Array.from(this.results.querySelectorAll('.product-card--skeleton')), {
          duration: 0.24,
          stagger: 0.018,
          y: 8
        });
      }

      renderFilterSkeleton() {
        if (!this.facets) return;
        this.facets.innerHTML = Array.from({ length: 6 }).map(() => `
          <div class="luxe-search__facet luxe-search__facet--skeleton" aria-hidden="true">
            <div class="luxe-search__filter-skeleton-heading">
              <span class="luxe-skeleton luxe-search__filter-skeleton-title"></span>
              <span class="luxe-skeleton luxe-search__filter-skeleton-count"></span>
            </div>
            <div class="luxe-search__filter-skeleton-options">
              <span class="luxe-skeleton luxe-search__filter-skeleton-option"></span>
              <span class="luxe-skeleton luxe-search__filter-skeleton-option luxe-search__filter-skeleton-option--wide"></span>
              <span class="luxe-skeleton luxe-search__filter-skeleton-option"></span>
            </div>
          </div>
        `).join('');
      }

      loadMoreSkeletonCard() {
        return `
          <article class="product-card product-card--skeleton product-card--load-skeleton" aria-hidden="true" data-load-more-skeleton>
            <div class="product-card__frame">
              <span class="product-card__media luxe-skeleton product-card__skeleton-media"></span>
            </div>
            <div class="product-card__content">
              <div class="product-card__details product-card__skeleton-details">
                <span class="luxe-skeleton product-card__skeleton-line product-card__skeleton-line--brand"></span>
                <span class="luxe-skeleton product-card__skeleton-line"></span>
                <span class="luxe-skeleton product-card__skeleton-line product-card__skeleton-line--short"></span>
                <span class="luxe-skeleton product-card__skeleton-line product-card__skeleton-line--price"></span>
              </div>
            </div>
          </article>
        `;
      }

      clearLoadMoreSkeletons() {
        if (!this.results) return;
        this.results.querySelectorAll('[data-load-more-skeleton]').forEach((node) => node.remove());
      }

      renderLoadMoreSkeleton() {
        if (!this.results || this.results.querySelector('[data-load-more-skeleton]')) return;
        const count = Math.min(Math.max(4, Math.floor((this.perPage || 8) / 2)), 8);
        this.results.insertAdjacentHTML('beforeend', Array.from({ length: count }).map(() => this.loadMoreSkeletonCard()).join(''));
        this.animateProductCards(Array.from(this.results.querySelectorAll('[data-load-more-skeleton]')), {
          duration: 0.26,
          stagger: 0.018,
          y: 10
        });
      }

      async search(pushState, trigger = pushState ? 'search_page_submit' : 'search_page_load') {
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();
        this.loadMoreToken = '';
        this.loadingMore = false;
        const preserveExistingGrid = trigger === 'search_page_load' && !!this.results?.querySelector('[data-product-card]');
        this.classList.add('is-loading');
        this.classList.toggle('is-hydrating', preserveExistingGrid);
        this.classList.remove('is-loading-more');
        this.renderStatus('Loading products...');
        if (preserveExistingGrid && this.results) {
          this.results.style.minHeight = `${this.results.offsetHeight}px`;
        } else {
          this.renderProductSkeleton();
        }
        this.renderFilterSkeleton();
        this.currentSearchId = makeAnalyticsId('search');
        const apiUrl = this.buildApiUrl();
        if (searchApiReadOnlyTriggers.has(trigger)) apiUrl.searchParams.set('analytics', 'false');
        appendSearchAnalyticsParams(apiUrl, {
          source: this.mode === 'collection' ? 'collection_search' : 'search_page',
          trigger,
          search_id: this.currentSearchId
        });

        try {
          const response = await fetch(apiUrl, {
            signal: this.abortController.signal,
            headers: { Accept: 'application/json' }
          });
          const payload = await response.json();
          if (!response.ok || payload.status === 'error') {
            throw new Error(payload.message || `Search failed with ${response.status}`);
          }

          this.payload = payload;
          this.lastPayload = payload;
          this.render(payload);
          if (trigger === 'search_page_typeahead') {
            this.queueSearchIntent(this.state.q, payload, 'search_page_settled');
          } else if (trigger === 'search_page_submit') {
            clearTimeout(this.intentTimer);
            this.trackSearchIntent(this.state.q, payload, 'search_page_submit');
          }

          if (pushState) {
            window.history.pushState({}, '', this.buildPageUrl());
          }
        } catch (error) {
          if (error.name === 'AbortError') return;
          this.renderStatus(error.message || 'Search failed.');
        } finally {
          this.classList.remove('is-loading');
          if (preserveExistingGrid && this.results) {
            window.requestAnimationFrame(() => {
              this.results.style.minHeight = '';
              this.classList.remove('is-hydrating');
            });
          } else {
            this.classList.remove('is-hydrating');
          }
        }
      }

      render(payload, append = false) {
        const count = Number(payload.found) || 0;
        const q = payload.query?.q && payload.query.q !== '*' ? ` for "${escapeHtml(payload.query.q)}"` : '';
        if (this.count) this.count.innerHTML = `${new Intl.NumberFormat('en-US').format(count)} products${q}`;
        this.totalFound = count;
        this.hasMore = this.state.page * this.perPage < count;
        this.renderResults(payload.hits || [], append);
        if (!append) {
          this.renderFacets(payload.facets || []);
          this.renderActiveFilters();
        }
        this.renderPagination(count);
        this.renderInfiniteState();
        this.renderStatus('');
        this.updateFilterControls();
      }

      renderStatus(message) {
        if (!this.status) return;
        this.status.textContent = message;
        this.status.hidden = !message;
      }

      renderResults(hits, append = false) {
        if (!this.results) return;
        if (append) this.clearLoadMoreSkeletons();
        if (!hits.length) {
          if (!append) this.results.innerHTML = '<p class="luxe-search__empty">No matching products found.</p>';
          return;
        }

        if (!append) this.renderedProductKeys = new Set();
        const existingCount = append ? this.results.querySelectorAll('[data-product-card]').length : 0;
        const cards = [];

        hits.forEach((product, index) => {
          // A unit card (a model with two or more units for sale shows each in searches) is its own card.
          const key = (product.card === 'unit' && product.variant_id ? `${product.handle}?variant=${product.variant_id}` : product.handle) || product.url || product.id || `${product.title || 'product'}-${index}`;
          if (this.renderedProductKeys.has(key)) return;
          this.renderedProductKeys.add(key);
          cards.push(this.productCard(product, index));
        });

        if (!cards.length) return;
        if (append) {
          this.results.insertAdjacentHTML('beforeend', cards.join(''));
        } else {
          this.results.innerHTML = cards.join('');
        }

        const newCards = Array.from(this.results.querySelectorAll('[data-product-card]')).slice(existingCount);
        newCards.forEach((card) => initProductCardGalleries(card));
        const mobileAppend = append && this.isMobileFilterMode();
        this.animateProductCards(newCards, this.classList.contains('is-hydrating') ? {
          duration: 0.18,
          scale: 1,
          stagger: 0.006,
          y: 4
        } : mobileAppend ? {
          duration: 0.16,
          scale: 1,
          stagger: 0.004,
          y: 4
        } : {});
      }

      animateProductCards(cards, options = {}) {
        const {
          duration = 0.38,
          scale = 0.985,
          stagger = 0.022,
          y = 16
        } = options;
        animateIn(cards, { duration, scale, stagger, y, limit: 36, easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)' });
      }

      productImages(product) {
        const getImageSrc = (candidate) => {
          if (!candidate) return '';
          if (typeof candidate === 'string') return candidate;
          return candidate.src || candidate.url || candidate.image || '';
        };
        const images = [
          product.image,
          product.secondary_image,
          product.image_secondary,
          ...(Array.isArray(product.images) ? product.images : [])
        ].map(getImageSrc).filter(Boolean);
        const seen = new Set();
        return images.filter((src) => {
          if (seen.has(src)) return false;
          seen.add(src);
          return true;
        });
      }

      productMobileGallery(images, imageAlt, eagerFirstSlide = false) {
        const gallery = images.slice(0, 3);
        if (gallery.length < 2) return '';
        const slides = [...gallery, ...gallery];
        const eagerIndex = eagerFirstSlide ? 0 : -1;
        const slideMarkup = slides.map((src, index) => `
          <img
            class="product-card__mobile-gallery-slide"
            src="${escapeHtml(src)}"
            alt="${index >= gallery.length ? '' : imageAlt}"
            loading="${index === eagerIndex ? 'eager' : 'lazy'}"
            decoding="async"
            fetchpriority="${index === eagerIndex ? 'high' : 'low'}"
            draggable="false"
            data-gallery-slide
            ${index >= gallery.length ? 'data-gallery-clone' : ''}
          >
        `).join('');

        return `
          <span class="product-card__mobile-gallery" data-product-card-gallery data-slide-count="${gallery.length}">
            <span class="product-card__mobile-gallery-track" data-product-card-gallery-track>${slideMarkup}</span>
            <span class="product-card__mobile-gallery-indicator" aria-hidden="true">
              <span class="product-card__mobile-gallery-indicator-active" data-product-card-gallery-indicator style="width: ${100 / gallery.length}%;"></span>
            </span>
          </span>
        `;
      }

      productCard(product, index) {
        const rawTitle = String(product.title || '').trim();
        const rawDesigner = String(product.designer || product.brand || product.vendor || '').trim();
        let displayTitle = rawTitle;
        if (rawDesigner && rawTitle.toLowerCase().startsWith(rawDesigner.toLowerCase())) {
          displayTitle = rawTitle.slice(rawDesigner.length).trim();
          if (displayTitle.startsWith('-')) displayTitle = displayTitle.slice(1).trim();
        }
        if (!displayTitle) displayTitle = rawTitle;
        const title = escapeHtml(rawTitle);
        const displayTitleHtml = escapeHtml(displayTitle);
        const designer = escapeHtml(rawDesigner);
        const price = formatPrice(product.price_min || product.price);
        const rawPrice = product.price_min || product.price || '';
        const retail = formatPrice(product.retail_price);
        const condition = escapeHtml(product.condition || '');
        // A Trusted Partner's piece carries LDJ Partners' "Pre-order" tag: the same badge as the card snippet's.
        const preorder = (product.tags || []).some((tag) => String(tag).toLowerCase() === 'pre-order');
        const availability = product.availability === 'out_of_stock'
          ? '<span class="badge">Sold Out</span>'
          : preorder ? '<span class="badge badge--preorder">Pre-Order</span>' : '';
        // The theme's own heart (assets/icon-heart.svg), as on the home page and product page: an outline that fills when saved.
        const heartIcon = '<span class="icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true" focusable="false"><path d="M10.4107 19.9677C7.58942 17.858 2 13.0348 2 8.69444C2 5.82563 4.10526 3.5 7 3.5C8.5 3.5 10 4 12 6C14 4 15.5 3.5 17 3.5C19.8947 3.5 22 5.82563 22 8.69444C22 13.0348 16.4106 17.858 13.5893 19.9677C12.6399 20.6776 11.3601 20.6776 10.4107 19.9677Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/></svg></span>';
        // Saved pieces only while the theme loads them (settings.wishlist_enabled, layout/theme.liquid).
        const wishlistOn = Boolean(document.querySelector('script[data-ldj-wishlist]'));
        const imageAlt = escapeHtml(product.image_alt || rawTitle);
        const imageSources = this.productImages(product);
        const primaryImage = imageSources[0] || '';
        const secondaryImage = imageSources[1] || '';
        const productPosition = ((Math.max(this.state.page, 1) - 1) * this.perPage) + index + 1;
        const eagerMedia = this.state.page <= 1 && productPosition <= 4;
        const mobileGallery = this.productMobileGallery(imageSources, imageAlt, eagerMedia);
        const image = primaryImage
          ? `
            <span class="product-card__image-wrapper fp-image-wrapper">
              <img class="product-card__image product-card__image--primary" src="${escapeHtml(primaryImage)}" alt="${imageAlt}" loading="${eagerMedia ? 'eager' : 'lazy'}" decoding="async" fetchpriority="${eagerMedia ? 'high' : 'low'}">
              ${secondaryImage ? `<img class="product-card__image product-card__image--secondary" src="${escapeHtml(secondaryImage)}" alt="${imageAlt}" loading="lazy" decoding="async" fetchpriority="low">` : ''}
            </span>
          `
          : '';
        const productAnalyticsAttributes = `
          data-search-product-link
          data-search-source="${this.mode === 'collection' ? 'collection_search' : 'search_page'}"
          data-search-id="${escapeHtml(this.currentSearchId)}"
          data-search-query="${escapeHtml(this.state.q || '')}"
          data-product-handle="${escapeHtml(product.handle || '')}"
          data-product-title="${title}"
          data-product-url="${escapeHtml(product.url || '')}"
          data-product-position="${productPosition}"
          data-product-price="${escapeHtml(rawPrice)}"
        `;

        return `
          <article class="product-card card-wrapper product-card-wrapper luxe-product-card${secondaryImage ? ' product-card--has-secondary' : ''}${mobileGallery ? ' product-card--has-mobile-gallery' : ''}" data-product-card data-product-handle="${escapeHtml(product.handle || '')}">
            ${wishlistOn ? `<button class="product-card__wishlist" type="button" aria-label="Add ${displayTitleHtml} to wishlist" data-product-card-wishlist>
              ${heartIcon}
            </button>` : ''}
            <div class="product-card__frame card card--standard card--media">
              <a class="product-card__media" href="${escapeHtml(product.url)}" aria-label="${title}" ${productAnalyticsAttributes}>
                ${image}
                ${mobileGallery}
              </a>
              <div class="product-card__badges">
                ${availability}
              </div>
            </div>
            <div class="product-card__content card__content underline-links-hover">
              <div class="product-card__details ais-hit--details card__information">
                <h3 class="product-card__title fp-title card__heading">
                  ${designer ? `<span class="product-card__vendor fp-card__vendor">${designer}</span>` : ''}
                <a class="product-card__link full-unstyled-link fp-card__link" href="${escapeHtml(product.url)}" ${productAnalyticsAttributes}>
                  <span class="fp-card__link__product-name">${displayTitleHtml}</span>
                </a>
                </h3>
                ${condition ? `<p class="product-card__condition fp-condition left"><span class="product-card__condition-label">Condition: </span>${condition}</p>` : '<p class="product-card__condition product-card__condition--empty" aria-hidden="true">&nbsp;</p>'}
                ${price ? `<div class="product-card__price price">${price}</div>` : ''}
                ${retail ? `<p class="product-card__retail">Retail: ${retail}</p>` : ''}
              </div>
            </div>
          </article>
        `;
      }

      renderFacets(facets, { animate = true } = {}) {
        if (!this.facets) return;
        this.facetDraftDirty = false;
        const cleanFacets = facets.map((facet) => {
          if (facet.type === 'range') return facet;
          return {
            ...facet,
            values: (facet.values || []).filter((item) => this.shouldShowFacetValue(facet, item.value))
          };
        });
        const visibleFacets = cleanFacets.filter((facet) => {
          if (this.collection && facet.field === 'collection_titles') return false;
          // Not offered as filters (LDJ, 9 Oct 2026). The search app still sends them (the search
          // box's suggestions use them) and a link that already carries one still applies it.
          if (luxeHiddenFacets.includes(facet.param || facet.field)) return false;
          return facet.type === 'range' || facet.values?.length;
        });

        if (!visibleFacets.length) {
          this.facets.innerHTML = '<p class="text-sm text-ink/60">No filters available.</p>';
          this.updateFilterControls();
          return;
        }

        this.facets.innerHTML = visibleFacets.map((facet, index) => this.facetGroup(facet, index < 2)).join('');
        this.updateFilterControls();
        // Phones: the closed sheet is invisible, and it staggers its groups when it opens.
        if (animate && !this.isMobileFilterMode()) this.animateFilterFacets();
      }

      facetGroup(facet, open) {
        const values = (facet.values || []).filter((item) => this.shouldShowFacetValue(facet, item.value)).slice(0, 50);
        const body = facet.type === 'range'
          ? values.map((item, index) => this.facetCheckbox('price', item.value, formatFacetLabel(item.value), item.count, index >= luxeFacetCollapseLimit)).join('')
          : values.map((item, index) => this.facetCheckbox(facet.param, item.value, item.value, item.count, index >= luxeFacetCollapseLimit)).join('');
        const hiddenCount = Math.max(values.length - luxeFacetCollapseLimit, 0);
        const moreButton = hiddenCount > 0
          ? `<button class="luxe-search__facet-more" type="button" data-facet-more aria-expanded="false" data-more-label="Show ${hiddenCount} more" data-less-label="Show less">Show ${hiddenCount} more</button>`
          : '';

        return `
          <details class="luxe-search__facet" ${open ? 'open' : ''}>
            <summary>
              <span>${escapeHtml(facet.label)}</span>
              <span class="luxe-search__facet-count">${values.length}</span>
            </summary>
            <div class="luxe-search__facet-options">
              ${body}
              ${moreButton}
            </div>
          </details>
        `;
      }

      shouldShowFacetValue(facet, value) {
        if (!value || isTaxonomyTerm(value)) return false;
        const param = facet.param || facet.field || '';
        if ((param === 'category' || param === 'type') && normalizeQuery(value).length > 42) return false;
        return true;
      }

      facetCheckbox(param, value, label, count, collapsed = false) {
        const checked = param === 'price'
          ? this.isPriceActive(value)
          : this.state.filters[param]?.has(value);

        return `
          <label class="luxe-search__facet-option" ${collapsed ? 'data-facet-extra hidden' : ''}>
            <input
              type="checkbox"
              data-filter-input
              data-filter-param="${escapeHtml(param)}"
              data-filter-value="${escapeHtml(value)}"
              ${checked ? 'checked' : ''}
            >
            <span>${escapeHtml(label)}</span>
            <span>${Number(count) || 0}</span>
          </label>
        `;
      }

      isPriceActive(value) {
        const range = luxePriceRanges[value] || {};
        return (this.state.priceMin || '') === (range.min || '') && (this.state.priceMax || '') === (range.max || '');
      }

      renderActiveFilters() {
        if (!this.activeFilters) return;
        const chips = [];

        for (const [param, values] of Object.entries(this.state.filters)) {
          for (const value of values) {
            chips.push(this.filterChip(param, value, value));
          }
        }

        if (this.state.priceMin || this.state.priceMax) {
          const label = this.priceChipLabel();
          chips.push(this.filterChip('price', '', label));
        }

        this.activeFilters.innerHTML = chips.join('');
        this.updateFilterControls();
      }

      setupInfiniteScroll() {
        if (!this.enableInfinite || !this.sentinel || !('IntersectionObserver' in window)) return;
        const rootMargin = this.isMobileFilterMode() ? '360px 0px 520px' : '900px 0px 1200px';
        this.infiniteObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
        }, {
          rootMargin
        });
        this.infiniteObserver.observe(this.sentinel);
      }

      async loadNextPage() {
        if (!this.enableInfinite || this.loadingMore || !this.hasMore || this.classList.contains('is-loading')) return;
        this.refreshPerPage();
        const currentRendered = this.results?.querySelectorAll('[data-product-card]').length || 0;
        if (this.isMobileFilterMode() && currentRendered >= this.maxMobileRendered) {
          this.hasMore = false;
          this.renderInfiniteState();
          this.renderStatus('Refine filters to keep browsing smoothly.');
          return;
        }
        const previousPage = this.state.page;
        const token = makeAnalyticsId('load');
        this.loadMoreToken = token;
        this.loadingMore = true;
        this.state.page = previousPage + 1;
        this.classList.add('is-loading-more');
        this.renderInfiniteState();
        this.renderLoadMoreSkeleton();

        const apiUrl = this.buildApiUrl();
        apiUrl.searchParams.set('analytics', 'false');
        appendSearchAnalyticsParams(apiUrl, {
          source: this.mode === 'collection' ? 'collection_search' : 'search_page',
          trigger: 'infinite_scroll',
          search_id: this.currentSearchId
        });

        try {
          const response = await fetch(apiUrl, { headers: { Accept: 'application/json' } });
          const payload = await response.json();
          if (!response.ok || payload.status === 'error') {
            throw new Error(payload.message || `Search failed with ${response.status}`);
          }
          if (this.loadMoreToken !== token) return;
          this.payload = payload;
          this.lastPayload = payload;
          this.render(payload, true);
        } catch (error) {
          if (this.loadMoreToken === token) {
            this.state.page = previousPage;
            this.clearLoadMoreSkeletons();
            this.renderStatus(error.message || 'Could not load more products.');
          }
        } finally {
          if (this.loadMoreToken === token) {
            this.loadingMore = false;
            this.clearLoadMoreSkeletons();
            this.classList.remove('is-loading-more');
            this.renderInfiniteState();
          }
        }
      }

      renderInfiniteState() {
        if (!this.sentinel) return;
        if (!this.enableInfinite) {
          this.sentinel.hidden = true;
          return;
        }

        const label = this.sentinel.querySelector('span');
        this.sentinel.hidden = !this.loadingMore && !this.hasMore;
        if (label) label.textContent = this.loadingMore ? 'Loading' : 'More products available';
      }

      priceChipLabel() {
        const match = Object.entries(luxePriceRanges).find(([, range]) => {
          return (this.state.priceMin || '') === (range.min || '') && (this.state.priceMax || '') === (range.max || '');
        });
        if (match) return match[1].label;
        if (this.state.priceMin && this.state.priceMax) return `${formatPrice(this.state.priceMin)} - ${formatPrice(this.state.priceMax)}`;
        if (this.state.priceMin) return `${formatPrice(this.state.priceMin)}+`;
        return `Under ${formatPrice(this.state.priceMax)}`;
      }

      filterChip(param, value, label) {
        return `
          <button class="luxe-search__chip" type="button" data-remove-filter="${escapeHtml(param)}" data-remove-value="${escapeHtml(value)}">
            ${escapeHtml(formatFacetLabel(label))}
            <span aria-hidden="true">×</span>
          </button>
        `;
      }

      renderPagination(found) {
        if (!this.pagination) return;
        if (this.enableInfinite) {
          this.pagination.innerHTML = '';
          this.pagination.hidden = true;
          return;
        }
        this.pagination.hidden = false;
        const pages = Math.ceil(found / this.perPage);
        if (pages <= 1) {
          this.pagination.innerHTML = '';
          return;
        }

        const current = Math.min(this.state.page, pages);
        this.pagination.innerHTML = `
          <button class="button button--secondary" type="button" data-page="${Math.max(1, current - 1)}" ${current <= 1 ? 'disabled' : ''}>Previous</button>
          <span>Page ${current} of ${pages}</span>
          <button class="button button--secondary" type="button" data-page="${Math.min(pages, current + 1)}" ${current >= pages ? 'disabled' : ''}>Next</button>
        `;
      }

      queueSearchIntent(term, payload, trigger = 'search_page_settled') {
        clearTimeout(this.intentTimer);
        this.intentTimer = setTimeout(() => {
          this.trackSearchIntent(term, payload, trigger);
        }, searchIntentDelay);
      }

      trackSearchIntent(term, payload, trigger = 'search_page_settled') {
        const query = normalizeQuery(term);
        if (!shouldTrackSearchIntent(query)) return;
        const resultCount = resultCountFor(payload);
        const intentKey = [
          searchIntentTypeFor(payload),
          query.toLowerCase(),
          resultCount ?? ''
        ].join(':');
        const now = Date.now();
        if (this.lastIntentKey === intentKey && now - this.lastIntentAt < searchIntentDedupeMs) return;

        this.lastIntentKey = intentKey;
        this.lastIntentAt = now;
        this.track(searchIntentTypeFor(payload), {
          query,
          trigger,
          result_count: resultCount,
          page: payload?.page,
          per_page: payload?.query?.perPage,
          sort: payload?.query?.sort,
          filters: this.analyticsFilters(),
          search_time_ms: payload?.searchTimeMs,
          search_id: this.currentSearchId
        });
      }

      analyticsFilters() {
        const filters = {};
        for (const [param, values] of Object.entries(this.state.filters || {})) {
          filters[param] = Array.from(values);
        }
        if (this.state.priceMin) filters.price_min = this.state.priceMin;
        if (this.state.priceMax) filters.price_max = this.state.priceMax;
        if (this.collection) filters.collection = this.collection;
        if (this.collectionTitle) filters.collection_scope = this.collectionTitle;
        return filters;
      }

      track(type, payload = {}) {
        trackSearchEvent(type, {
          source: this.mode === 'collection' ? 'collection_search' : 'search_page',
          ...payload
        }, this.eventsEndpoint);
      }
    });
  }
})();
